import { ORGANIZATION, SITE_NAME, SITE_NAME_FA, SITE_URL } from './config';
import { absoluteUrl } from './urls';

export const buildOrganizationSchema = (language = 'fa') => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: language === 'fa' ? SITE_NAME_FA : ORGANIZATION.name,
  legalName: ORGANIZATION.legalName,
  url: ORGANIZATION.url,
  logo: ORGANIZATION.logo,
  sameAs: ORGANIZATION.sameAs,
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: ORGANIZATION.contactPoint.telephone,
    contactType: ORGANIZATION.contactPoint.contactType,
    availableLanguage: ORGANIZATION.contactPoint.availableLanguage
  }
});

export const buildWebSiteSchema = (language = 'fa') => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: language === 'fa' ? SITE_NAME_FA : SITE_NAME,
  url: SITE_URL,
  inLanguage: language === 'fa' ? 'fa-IR' : 'en-US',
  publisher: {
    '@type': 'Organization',
    name: language === 'fa' ? SITE_NAME_FA : ORGANIZATION.name,
    url: ORGANIZATION.url
  }
});

export const buildSoftwareApplicationSchema = (language = 'fa') => ({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: language === 'fa' ? SITE_NAME_FA : SITE_NAME,
  applicationCategory: 'DeveloperApplication',
  operatingSystem: 'Web',
  url: SITE_URL,
  description:
    language === 'fa'
      ? 'پلتفرم ساخت محصول با هوش مصنوعی برای SaaS، وبسایت، فروشگاه آنلاین و MVP.'
      : 'AI product builder for SaaS apps, websites, online shops, and MVPs with live preview and instant deployment.',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
    description: language === 'fa' ? 'شروع رایگان' : 'Free to start'
  },
  featureList: language === 'fa'
    ? ['پیش‌نمایش زنده', 'استقرار فوری', 'دامنه اختصاصی', 'یکپارچگی Supabase', 'درگاه پرداخت', 'AI Agent']
    : ['Live sandbox preview', 'Instant deployment', 'Custom domains', 'Supabase integration', 'Payment gateways', 'AI agents']
});

export const buildAboutPageSchema = (language = 'fa') => ({
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: language === 'fa' ? `درباره ${SITE_NAME_FA}` : `About ${SITE_NAME}`,
  url: absoluteUrl('/about', language),
  inLanguage: language === 'fa' ? 'fa-IR' : 'en-US',
  isPartOf: {
    '@type': 'WebSite',
    name: language === 'fa' ? SITE_NAME_FA : SITE_NAME,
    url: SITE_URL
  },
  about: buildOrganizationSchema(language)
});

export const buildPricingPageSchema = (language = 'fa') => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: language === 'fa' ? `تعرفه ${SITE_NAME_FA}` : `${SITE_NAME} Pricing`,
  url: absoluteUrl('/pricing', language),
  inLanguage: language === 'fa' ? 'fa-IR' : 'en-US',
  isPartOf: {
    '@type': 'WebSite',
    name: language === 'fa' ? SITE_NAME_FA : SITE_NAME,
    url: SITE_URL
  },
  about: buildOrganizationSchema(language)
});

export const buildFaqSchema = (items = []) => {
  if (!items.length) {
    return null;
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  };
};

export const buildHomeStructuredData = (language = 'fa', faqItems = []) => {
  const schemas = [
    buildOrganizationSchema(language),
    buildWebSiteSchema(language),
    buildSoftwareApplicationSchema(language)
  ];
  const faqSchema = buildFaqSchema(faqItems);

  if (faqSchema) {
    schemas.push(faqSchema);
  }

  return schemas;
};

export const buildPageStructuredData = ({ page, language, faqItems = [] }) => {
  switch (page) {
    case 'home':
      return buildHomeStructuredData(language, faqItems);
    case 'about':
      return [buildOrganizationSchema(language), buildAboutPageSchema(language)];
    case 'pricing': {
      const schemas = [buildOrganizationSchema(language), buildPricingPageSchema(language)];
      const faqSchema = buildFaqSchema(faqItems);

      if (faqSchema) {
        schemas.push(faqSchema);
      }

      return schemas;
    }
    default:
      return [];
  }
};
